import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Alert } from 'antd';
import staffAuthManager from '../../firebase/staffAuthService';

const StaffProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState('loading');
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Check if bypass mode is enabled
    if (staffAuthManager.isBypassMode()) {
      const mockEmployee = staffAuthManager.getMockEmployee();
      setAuthState('authenticated');
      setEmployee(mockEmployee);
      setError(null);
      return;
    }

    // Initialize auth listener for normal mode
    const handleAuthStateChange = (state, employeeData) => {
      setAuthState(state);
      setEmployee(employeeData);
      
      if (state === 'unauthorized') {
        setError('Access denied. Employee credentials required.');
      } else if (state === 'no-profile') {
        setError('Employee profile not found. Please contact administrator.');
      } else {
        setError(null);
      }
    };

    staffAuthManager.initializeAuthListener(handleAuthStateChange);

    // Cleanup on unmount
    return () => {
      staffAuthManager.cleanup();
    };
  }, []);

  // Show loading spinner while checking authentication
  if (authState === 'loading' || authState === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>
          Verifying employee credentials...
        </p>
      </div>
    );
  }

  // Show error for unauthorized access
  if (authState === 'unauthorized' || authState === 'no-profile') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px'
      }}>
        <Alert
          message="Access Denied"
          description={error}
          type="error"
          showIcon
          action={
            <a href="/staff-login" style={{ textDecoration: 'underline' }}>
              Go to Staff Login
            </a>
          }
        />
      </div>
    );
  }

  // Redirect to staff login if not authenticated
  if (authState === 'unauthenticated') {
    return <Navigate to="/staff-login" state={{ from: location }} replace />;
  }

  // Render protected content if authenticated
  if (authState === 'authenticated' && employee) {
    return children;
  }

  // Fallback - redirect to staff login
  return <Navigate to="/staff-login" state={{ from: location }} replace />;
};

export default StaffProtectedRoute;
